import type { ComponentType } from 'react';
import GitHubIcon from '@material-ui/icons/GitHub';
import DnsIcon from '@material-ui/icons/Dns';
import LockIcon from '@material-ui/icons/LockOutlined';
import LayersIcon from '@material-ui/icons/LayersOutlined';
import AccountTreeIcon from '@material-ui/icons/AccountTreeOutlined';
import VpnKeyIcon from '@material-ui/icons/VpnKeyOutlined';
import PeopleIcon from '@material-ui/icons/PeopleOutline';
import PowerIcon from '@material-ui/icons/PowerOutlined';
import {
  SYSTEM_TITLES,
  type SystemId,
} from '@revenexx/backstage-plugin-access-common';

/**
 * The eight ways to ask for access (DX-342; promised in specs/access.md and
 * specs/access-request.md) — everything the cards and the request panel offer,
 * in one place, so a decision about a scope or a duration changes this file and
 * nothing else.
 *
 * The cards' words are the target picture's (Michael, 24 September 2026), with
 * one deliberate change: where it named a fixed approver the catalog does not
 * hold — "Platform Team" for the platform's own systems, "Group owner",
 * "Operations" — the card says who really answers, because the panel reads the
 * approver from the catalog and a card naming somebody else would contradict
 * it.
 *
 * Scopes and durations are proposals: the target picture shows one of each
 * (read, seven days). They are derived from the cards' own descriptions and the
 * roles each system documents, and wait on the target picture's author
 * (specs/access-request.md, Gaps).
 *
 * The backend routes on the system id and the target alone. The ids are
 * shared with it (@revenexx/backstage-plugin-access-common), and systems.test.ts
 * holds that every one of them has a card here.
 */

export type { SystemId } from '@revenexx/backstage-plugin-access-common';

export type Scope = {
  label: string;
  /** Reaches production, so it is only offered with an end. */
  production?: boolean;
  /** A repository that does not exist yet: named, not picked. */
  newRepository?: boolean;
};

/** Where a request's target is picked from. */
export type TargetSource =
  /**
   * The repositories the catalog knows, one entry each however many components
   * one holds — access to a repository, and an Infisical project, is per
   * repository.
   */
  | { kind: 'repositories' }
  /** Components the catalog holds a repository for, one entry each. */
  | { kind: 'services' }
  /**
   * The platform's own systems, by their catalog refs. They are Resources from
   * `revenexx/platform-systems` (PE-1002): a `component:` ref finds nothing, and
   * the request then goes to Developer Experience as having no owner.
   */
  | { kind: 'platform'; refs: string[] }
  /** The teams the catalog holds. */
  | { kind: 'groups' }
  /** A fixed list — the tools a seat can be asked for in. */
  | { kind: 'list'; items: string[] };

export type AccessSystem = {
  id: SystemId;
  title: string;
  /** What the card says the system covers. */
  description: string;
  /** Who the card says answers. */
  approver: string;
  /** How soon an answer typically comes, as the card says it. */
  typical: string;
  icon: ComponentType<{ fontSize?: 'small' | 'inherit' }>;
  targets: TargetSource;
  /** The scopes offered; empty where a request has none to choose. */
  scopes: Scope[];
  /** Asked as text rather than chosen — a service account's tenants. */
  scopeText?: { label: string; placeholder: string };
  /**
   * What the target is, in the panel's own sentences — "Choose the app first".
   * The field itself is "Target" for every system, as the target picture has it.
   */
  targetLabel: string;
};

/** The organisations a new repository can be asked for in. */
export const ORGANISATIONS = [
  'revenexx',
  'revenexx-sdks',
  'revenexx-integrations',
  'revenexx-apps',
];

export const SYSTEMS: AccessSystem[] = [
  {
    id: 'repository',
    title: SYSTEM_TITLES.repository,
    description: 'Read or write on a repo, or a new repo in an org',
    approver: 'Owner of the repo',
    typical: '~2 h',
    icon: GitHubIcon,
    targets: { kind: 'repositories' },
    targetLabel: 'Repository',
    scopes: [
      { label: 'Read' },
      { label: 'Write' },
      { label: 'New repository', newRepository: true },
    ],
  },
  {
    id: 'infrastructure',
    title: SYSTEM_TITLES.infrastructure,
    description: 'Portainer stacks, Swarm nodes, deploy rights',
    approver: 'Owner of the system',
    typical: '~1 d · time-boxed',
    icon: DnsIcon,
    targets: {
      kind: 'platform',
      refs: ['resource:default/portainer', 'resource:default/swarm'],
    },
    targetLabel: 'Platform system',
    // Only production exists today, so everything here reaches it.
    scopes: [
      { label: 'Read stacks', production: true },
      { label: 'Deploy stacks', production: true },
      { label: 'Swarm node', production: true },
    ],
  },
  {
    id: 'secrets',
    title: SYSTEM_TITLES.secrets,
    description: 'Infisical scope for an app or environment',
    approver: 'Owner of the app',
    typical: '~2 h',
    icon: LockIcon,
    // One Infisical project per repository, named like it (DX-16).
    targets: { kind: 'repositories' },
    targetLabel: 'App',
    scopes: [
      { label: 'dev · read' },
      { label: 'dev · write' },
      { label: 'staging · read' },
      { label: 'staging · write' },
      { label: 'prod · read', production: true },
      { label: 'prod · write', production: true },
    ],
  },
  {
    id: 'database',
    title: SYSTEM_TITLES.database,
    description: 'Read-only on a production database',
    approver: 'Owner of the system',
    typical: '~1 d · 7 days max',
    icon: LayersIcon,
    targets: {
      kind: 'platform',
      refs: [
        'resource:default/postgres',
        'resource:default/pgdog',
        'resource:default/mariadb',
      ],
    },
    targetLabel: 'Database server',
    scopes: [{ label: 'Read-only', production: true }],
  },
  {
    id: 'vpn',
    title: SYSTEM_TITLES.vpn,
    description: 'NetBird device, or a site-to-site peer',
    approver: 'Owner of the system',
    typical: '~4 h',
    icon: AccountTreeIcon,
    targets: {
      kind: 'platform',
      refs: ['resource:default/vpn-gateway-infra', 'resource:default/bastion'],
    },
    targetLabel: 'Platform system',
    scopes: [
      { label: 'Device' },
      { label: 'SSH to the nodes', production: true },
      { label: 'Site-to-site peer', production: true },
    ],
  },
  {
    id: 'service-account',
    title: SYSTEM_TITLES['service-account'],
    description: 'Zitadel machine user with tenant claims',
    approver: 'Owner of the service',
    typical: '~4 h',
    icon: VpnKeyIcon,
    // A machine user belongs to a service, not to the repository it lives in.
    targets: { kind: 'services' },
    targetLabel: 'Service',
    scopes: [],
    scopeText: { label: 'Tenants', placeholder: 'revenexx' },
  },
  {
    id: 'team-membership',
    title: SYSTEM_TITLES['team-membership'],
    description: 'Join a group so ownership resolves',
    approver: 'Developer Experience, for now',
    typical: '~1 h',
    icon: PeopleIcon,
    targets: { kind: 'groups' },
    targetLabel: 'Team',
    scopes: [],
  },
  {
    id: 'tool-seat',
    title: SYSTEM_TITLES['tool-seat'],
    description: 'Figma, PostHog, Passbolt, Claude',
    approver: 'Developer Experience, for now',
    typical: '~1 d',
    icon: PowerIcon,
    targets: {
      kind: 'list',
      items: ['Figma', 'PostHog', 'Passbolt', 'Claude'],
    },
    targetLabel: 'Tool',
    scopes: [],
  },
];

export const systemById = (id: SystemId): AccessSystem =>
  SYSTEMS.find(system => system.id === id) ?? SYSTEMS[0];

/**
 * The durations a request offers (specs/access-request.md AC-5): production
 * only with an end, a database for seven days, everything else permanently
 * too. Undefined where nothing is time-bound — a seat, a team, a new repository.
 */
export function durationsFor(
  system: AccessSystem,
  scope: Scope | undefined,
): string[] | undefined {
  if (system.id === 'database') return ['7 days'];
  if (scope?.newRepository) return undefined;
  if (!system.scopes.length && !system.scopeText) return undefined;
  const bounded = ['7 days', '30 days', '90 days'];
  return scope?.production ? bounded : [...bounded, 'Permanent'];
}
